import React, { useState } from 'react';
import { Dropdown, Pagination } from 'semantic-ui-react';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from '../../../common/components/Table';
import { PageSection } from 'common/styles/page';
import { StyledIconButton } from 'common/components/button/StyledButton';
import { ApiPortalV1EmployeeRecordsByPeriodAndValueRetrieveApiResponse } from 'store/generated/portal/employee-records';

interface OutcomesIndividualDataTableProps {
  employee_record_values: ApiPortalV1EmployeeRecordsByPeriodAndValueRetrieveApiResponse | undefined;
  selectedRecordName: string | null;
  onAddRecord: (employeeId: number, employeeName: string) => void;
  onEditRecord: (record: any, employeeName: string) => void;
  onDeleteRecord: (record: any) => void;
}

const OutcomesIndividualDataTable: React.FC<OutcomesIndividualDataTableProps> = ({
  employee_record_values,
  selectedRecordName,
  onAddRecord,
  onEditRecord,
  onDeleteRecord,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [nameFilter, setNameFilter] = useState('');
  const itemsPerPage = 8;

  return (
    <PageSection>
      <Table>
        <TableHeader>
          <TableRow>
            <HeaderCell width={8}>Name</HeaderCell>
            <HeaderCell width={6}>{selectedRecordName}</HeaderCell>
            <HeaderCell width={2}></HeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {employee_record_values && employee_record_values.length > 0 ? (() => {
            // Filter by name first
            const lowerNameFilter = nameFilter.trim().toLowerCase();
            const filteredRecords = employee_record_values.filter((record: any) => {
              if (!lowerNameFilter) return true;
              return (
                record.employee_first_name?.toLowerCase().startsWith(lowerNameFilter) ||
                record.employee_last_name?.toLowerCase().startsWith(lowerNameFilter)
              );
            });

            const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

            return paginatedRecords.map((record: any) => {
              const employeeName = `${record.employee_last_name || ''}, ${record.employee_first_name || ''}`.trim();
              const hasData = record.record_id !== null;

              return (
                <TableRow
                  key={record.employee_id}
                  $selectable
                  onClick={() => hasData ? onEditRecord(record, employeeName) : onAddRecord(record.employee_id, employeeName)}
                >
                  <TableCell $firstcol>{employeeName}</TableCell>
                  <TableCell>
                    {hasData ? record.value : '-'}
                  </TableCell>
                  <TableCell onClick={(e: { stopPropagation: () => any; }) => e.stopPropagation()}>
                    {hasData ? (
                      <Dropdown
                        button
                        icon="ellipsis vertical"
                        floating
                        className="icon"
                        style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                        direction="left"
                      >
                        <Dropdown.Menu>
                          <Dropdown.Item
                            icon="edit"
                            text="Edit"
                            onClick={() => onEditRecord(record, employeeName)}
                          />
                          <Dropdown.Item
                            icon="trash"
                            text="Delete"
                            onClick={() => onDeleteRecord(record)}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    ) : (
                      <StyledIconButton
                        icon="plus"
                        title="Add Record"
                        onClick={() => onAddRecord(record.employee_id, employeeName)}
                        color="green"
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            });
          })() : (
            <TableRow>
              <TableCell colSpan={3} style={{ textAlign: 'center', padding: '20px' }}>
                No employee data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {(() => {
        const lowerNameFilter = nameFilter.trim().toLowerCase();
        const filteredRecords = employee_record_values?.filter((record: any) => {
          if (!lowerNameFilter) return true;
          return (
            record.employee_first_name?.toLowerCase().startsWith(lowerNameFilter) ||
            record.employee_last_name?.toLowerCase().startsWith(lowerNameFilter)
          );
        }) || [];
        return (
          <PageSection display="flex" justifyContent="space-between" style={{ marginTop: '20px' }}>
            <input
              type="text"
              placeholder="Filter by name"
              value={nameFilter}
              onChange={e => {
                setNameFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                height: '38px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                padding: '0 10px',
                fontSize: '14px',
                minWidth: '300px',
              }}
            />

            <Pagination
              activePage={currentPage}
              totalPages={Math.ceil(filteredRecords.length / itemsPerPage)}
              onPageChange={(e, { activePage }) => setCurrentPage(activePage as number)}
              firstItem={null}
              lastItem={null}
              prevItem={{ content: '‹', icon: true }}
              nextItem={{ content: '›', icon: true }}
            />
          </PageSection>
        );
      })()}
    </PageSection>
  );
};

export default OutcomesIndividualDataTable;
